<script lang="ts">
import type { MonospaceFile } from '#layers/data-model/public';
import { tv } from 'tailwind-variants';

const overlay = tv({
	slots: {
		gradient: `
			absolute inset-x-0 h-12 from-neutral-800/25 to-transparent transition-all
			group-hover:h-full group-hover:from-neutral-800/50
		`,
		infoWrapper: 'absolute inset-x-0 px-3 py-2 text-sm text-white',
		additionalInfo: 'max-h-0 truncate transition-all duration-200 group-hover:max-h-20',
		actionOverlay: `
			absolute inset-0 flex items-center justify-center gap-3 opacity-0 transition-opacity duration-200
			group-focus-within:opacity-100
			group-hover:opacity-100
			touch:opacity-100
		`,
		action: 'bg-white text-black hover:bg-neutral-200 hover:text-black focus-visible:text-black active:text-black',
	},
	variants: {
		direction: {
			bottom: {
				gradient: 'bottom-0 bg-linear-to-t',
				infoWrapper: 'bottom-0',
			},
			top: {
				gradient: 'top-0 bg-linear-to-b',
				infoWrapper: 'top-0',
			},
		},
	},
});

export interface FileInfoActionOverlayAction {
	ariaLabel?: string;
	disabled?: boolean;
	download?: boolean | string;
	href?: string;
	icon: string;
	id?: string;
	label: string;
	onClick?: (event: MouseEvent) => void;
	target?: string;
}
</script>

<script setup lang="ts">
import { createReusableTemplate } from '@vueuse/core';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAppConfig } from '#app/config';
import { useWorkspaceAssetUrl } from '#layers/assets/composables/use-asset-url';
import { useFileInfo } from '#layers/assets/composables/use-file-info';

const {
	direction = 'bottom',
	download,
	file,
	leadingActions = [],
	showRemove = true,
	trailingActions = [],
	ui: uiOverrides,
} = defineProps<{
	file: MonospaceFile;
	direction?: 'top' | 'bottom';
	download?: boolean;
	showRemove?: boolean;
	leadingActions?: FileInfoActionOverlayAction[];
	trailingActions?: FileInfoActionOverlayAction[];
	ui?: Partial<typeof overlay['slots']>;
}>();

const emit = defineEmits<{
	remove: [];
}>();

const appConfig = useAppConfig();
const { t } = useI18n();
const assetURL = useWorkspaceAssetUrl(() => file);

const { metadataLine: fileInfoLine } = useFileInfo(() => file);

const ui = computed(() => overlay({
	direction,
}));

const actions = computed<FileInfoActionOverlayAction[]>(() => [
	...leadingActions,
	...(download
		? [{
				id: 'download',
				label: t('common.download'),
				download: true,
				href: assetURL.value,
				icon: appConfig.ui.icons.download,
				target: '_blank',
			}]
		: []),
	...(showRemove
		? [{
				id: 'remove',
				label: t('common.remove'),
				icon: appConfig.ui.icons.remove,
				onClick: () => emit('remove'),
			}]
		: []),
	...trailingActions,
]);

const [DefineAction, ReuseAction] = createReusableTemplate<{ action: FileInfoActionOverlayAction }>({ inheritAttrs: false });
</script>

<template>
	<DefineAction v-slot="{ action }">
		<DTooltip :text="action.label">
			<DButton
				:class="ui.action({ class: uiOverrides?.action })"
				color="neutral"
				size="xl"
				variant="ghost"
				rounded
				:aria-label="action.ariaLabel ?? action.label"
				:disabled="action.disabled"
				:download="action.download"
				:href="action.href"
				:icon="action.icon"
				:target="action.target"
				@click.stop="action.onClick?.($event)"
			/>
		</DTooltip>
	</DefineAction>

	<div
		:class="ui.gradient({ class: uiOverrides?.gradient })"
	/>

	<div
		v-if="file.fileName"
		:class="ui.infoWrapper({ class: uiOverrides?.infoWrapper })"
	>
		<p class="truncate font-medium">
			{{ file.fileName }}
		</p>
		<p
			v-if="fileInfoLine"
			:class="ui.additionalInfo({ class: uiOverrides?.additionalInfo })"
		>
			{{ fileInfoLine }}
		</p>
	</div>

	<div
		:class="ui.actionOverlay({ class: uiOverrides?.actionOverlay })"
	>
		<ReuseAction v-for="action in actions" :key="action.id ?? action.label" :action />
	</div>
</template>
